import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { channels, allMovies, allCategories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const channel = channels.find((ch) => ch.slug === slug);
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [filteredMovies, setFilteredMovies] = useState<MovieItem[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (!channel) return;
    let movies = allMovies.filter((m) => m.channel === channel.name);
    if (selectedCategory !== '全部') {
      movies = movies.filter((m) => m.category === selectedCategory);
    }
    setFilteredMovies(movies);
  }, [channel, selectedCategory]);

  if (!channel) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">频道未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的频道不存在</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${channel.name}在线观看`}
        description={`${channel.name} - ${channel.description}。麻花影视特色频道，高清在线观看，满足特定类型影视观看需求。`}
        keywords={`${channel.name},麻花影视,高清在线观看,特色影视频道`}
        canonicalUrl={`/category/${slug}`}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: channel.name },
                ]}
              />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-4 mb-3">
                <div
                  className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${channel.color}20` }}
                >
                  <i className={`${channel.icon} text-2xl`} style={{ color: channel.color }}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                    {channel.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{channel.description}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
              {allCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    selectedCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:bg-background-200"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={movie.imageUrl}
                        alt={movie.title}
                        title={`${movie.title} - ${movie.channel}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-md whitespace-nowrap">
                          {movie.rating}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base md:text-lg font-semibold text-foreground-900 mb-1 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                        {movie.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">{movie.description}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{movie.year}</span>
                        <span>·</span>
                        <span>{movie.duration}</span>
                        <span>·</span>
                        <span>{movie.views.toLocaleString()} 次播放</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-film-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类下暂无内容</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}